import { Inject, Injectable } from '@angular/core';
import {
  MeetingSource,
  MeetingSourceOrigin,
  MeetingSourceQuery,
  MeetingSourceStatus,
  MeetingsService,
} from '@vendasta/meetings';
import { Observable, ReplaySubject, combineLatest, concat, timer } from 'rxjs';
import { debounceTime, first, map, shareReplay, switchMap } from 'rxjs/operators';
import { MeetingIntegrationsServiceModule } from './meeting-integrations-service.module';
import {
  NAMESPACE_TOKEN,
  ORIGIN_TOKEN,
  RedirectInfo,
  RedirectInfoEntry,
  SOURCE_REDIRECT_TOKEN,
  USER_ID_TOKEN,
} from './meeting-integrations.data.module';

export type SourcesState = {
  [source in MeetingSource]: {
    status: MeetingSourceStatus;
    connectDisconnectUrls: {
      [key: string]: string;
    };
  };
};

interface LoadSourcesOptions {
  filterKeys: string[] | null;
  statusOnly: boolean;
}

@Injectable({
  providedIn: MeetingIntegrationsServiceModule,
})
export class MeetingIntegrationsStoreService {
  private readonly signalToCallLoadSources$$: ReplaySubject<LoadSourcesOptions> = new ReplaySubject();
  private readonly loadSourceResponse$: Observable<SourcesState>;

  constructor(
    @Inject(USER_ID_TOKEN) private readonly userId$: Observable<string>,
    @Inject(NAMESPACE_TOKEN) private readonly namespace$: Observable<string>,
    @Inject(ORIGIN_TOKEN) private readonly origin: MeetingSourceOrigin,
    @Inject(SOURCE_REDIRECT_TOKEN) private readonly sourceRedirectInfo$: Observable<RedirectInfo>,
    private readonly meetingsService: MeetingsService,
  ) {
    this.loadSourceResponse$ = this.signalToCallLoadSources$$.pipe(
      // Debounce by 1 second to avoid excessive number of calls
      debounceTime(1000),
      switchMap((options) => {
        // The backend updates the connection asynchronously - so there may be a race condition on page load.
        // Try and get around that by performing more calls over a preiod of time.
        // Yes, this is very hacky.
        return concat(
          this.loadSourcesCall(options),
          timer(1500).pipe(
            first(),
            switchMap(() => this.loadSourcesCall(options)),
          ),
          timer(3000).pipe(
            first(),
            switchMap(() => this.loadSourcesCall(options)),
          ),
          timer(5000).pipe(
            first(),
            switchMap(() => this.loadSourcesCall(options)),
          ),
        );
      }),
      shareReplay(1),
    );
  }

  /**
   * Triggers the List API to fetch connect/disconnect URLs.
   * @param filterKeys Optional array of redirect keys to load. If omitted, all keys are loaded.
   * @param statusOnly If true, only loads one key per source to get connection status (minimizes queries).
   */
  loadSources(filterKeys?: string[], statusOnly?: boolean): Observable<SourcesState> {
    this.signalToCallLoadSources$$.next({ filterKeys: filterKeys || null, statusOnly: statusOnly || false });
    return this.loadSourceResponse$;
  }

  private loadSourcesCall(options: LoadSourcesOptions): Observable<SourcesState> {
    const { filterKeys, statusOnly } = options;
    const keysWithQueries$ = this.sourceRedirectInfo$.pipe(
      map((redirectInfo) => {
        return [
          MeetingSource.MEETING_SOURCE_GOOGLE_MEET,
          MeetingSource.MEETING_SOURCE_ZOOM,
          MeetingSource.MEETING_SOURCE_MICROSOFT,
        ]
          .map((source) => {
            const matchingKeys = Object.keys(redirectInfo).filter((key) => {
              if (filterKeys && !filterKeys.includes(key)) {
                return false;
              }
              return this.isKeyApplicableToSource(redirectInfo[key], source);
            });

            // In statusOnly mode, only use the first matching key per source (just to get status)
            const keysToUse = statusOnly && matchingKeys.length > 0 ? [matchingKeys[0]] : matchingKeys;

            return keysToUse.map((key) => {
              return {
                query: {
                  source,
                  connectNextUrl: redirectInfo[key].connectionSuccessfulReturnURL,
                  disconnectNextUrl: redirectInfo[key].disconnectSuccessReturnURL,
                },
                key: key,
              };
            });
          })
          .reduce((acc, curr) => [...acc, ...curr], []); // flatten array
      }),
    );

    return combineLatest([keysWithQueries$, this.userId$, this.namespace$]).pipe(
      switchMap(([keyWithQuery, userId, namespace]) => {
        return this.meetingsService
          .listForUser(
            userId,
            namespace,
            this.origin,
            keyWithQuery.map((k) => new MeetingSourceQuery(k.query)),
          )
          .pipe(
            map((response) => {
              const sourceQueryResponses = response.sourceInfo;
              return sourceQueryResponses.reduce((responseMap, sourceQuery, currentIndex) => {
                return {
                  ...responseMap,
                  [sourceQuery.source]: {
                    ...responseMap[sourceQuery.source],
                    status: sourceQuery.status,
                    connectDisconnectUrls: {
                      ...((responseMap[sourceQuery.source] || {}).connectDisconnectUrls || {}),
                      [keyWithQuery[currentIndex].key]: sourceQuery.connectDisconnectUrl,
                    },
                  },
                };
              }, {} as SourcesState);
            }),
          );
      }),
      shareReplay(1),
    );
  }

  /** Returns true if the redirect entry applies to the given source (or to all sources when unspecified). */
  private isKeyApplicableToSource(entry: RedirectInfoEntry, source: MeetingSource): boolean {
    return !entry.sources || entry.sources.includes(source);
  }
}
